import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Alert } from '@mui/material';

import Accordion from '~/components/ui/Accordion/Accordion';
import AsyncSwitch from '~/components/ui/AsyncSwitch';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { REDIRECT_QR_URI } from '~/constants/common';
import { QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME, QR_CODE_ID_PARAM_NAME } from '~/constants/qr-code';
import useQrCodeData from '~/hooks/useQrCodeData';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { COLORS } from '~/styles/colors';
import { DynamicQrCodeData } from '~/types/qrCode';

const defaultOrigin = `${REDIRECT_QR_URI}/s/`;

export default function SettingsBlockV2() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { control, watch, trigger, clearErrors, setValue } = useFormContext();

  const isAutoGeneratedUrlPath = 'settings.isAutoGeneratedUrl';
  const qrCodeUrlPath = 'settings.qrCodeUrl';
  const isScanLimitEnabledPath = 'settings.isScanLimitsEnabled';
  const scanLimitPath = 'settings.scanLimit';

  const isAutogeneratedUrl = watch(isAutoGeneratedUrlPath);
  const isScanLimitsEnabled = watch('settings.isScanLimitsEnabled');
  const isEditPage = !!searchParams.get(QR_CODE_BUILDER_EDIT_MODE_PARAM_NAME);
  const qrCodeId = searchParams.get(QR_CODE_ID_PARAM_NAME);

  const { data: qrCodeData } = useQrCodeData<DynamicQrCodeData>({ qrCodeId });

  return (
    <CreateQRContentBlockLayout type="settings" defaultExpanded={false}>
      <Accordion
        defaultExpanded
        variant="child"
        summary={
          <Text variant="t16Medium">
            {t('template0:title4')}{' '}
            <Text component="span" variant="inputSmall" sx={{ color: COLORS.grey400 }}>
              ({t('template0:subTitle3')})
            </Text>
          </Text>
        }
        details={
          <>
            <Controller
              control={control}
              name={isAutoGeneratedUrlPath}
              render={({ field: { value, onChange, onBlur } }) => {
                return (
                  <AsyncSwitch
                    id={isAutoGeneratedUrlPath}
                    sx={{ marginBottom: '8px' }}
                    checked={value}
                    onBlur={onBlur}
                    onChange={(event) => {
                      onChange(event);

                      if (event.target.checked) {
                        clearErrors(qrCodeUrlPath);

                        if (qrCodeData?.uri) {
                          setValue(qrCodeUrlPath, qrCodeData?.uri);
                        }
                      } else {
                        void trigger(qrCodeUrlPath);
                      }
                    }}
                    label={t('template0:sliderTitle1')}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name={qrCodeUrlPath}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                return (
                  <TextInput
                    id={qrCodeUrlPath}
                    value={isAutogeneratedUrl ? defaultOrigin : value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!error?.message}
                    helperText={error?.message}
                    inputProps={{ maxLength: isAutogeneratedUrl ? 2000 : 25 }}
                    fullWidth
                    label={t('labels:url')}
                    disabled={isAutogeneratedUrl}
                    InputPropsSx={
                      !isAutogeneratedUrl
                        ? {
                            '.MuiInputBase-input': {
                              padding: '10px 16px 10px 0',
                              height: '20px',
                            },
                          }
                        : undefined
                    }
                    InputProps={{
                      startAdornment: isAutogeneratedUrl ? undefined : <span>{defaultOrigin}</span>,
                    }}
                  />
                );
              }}
            />
            {isEditPage && (
              <Alert variant="filled" severity="warning">
                <span style={{ color: 'rgb(244, 241, 241)' }} className="MuiFormHelperText-root">
                  {t('template0:notification')}
                </span>
              </Alert>
            )}
          </>
        }
      />

      <Accordion
        defaultExpanded
        variant="child"
        hideBottomBorder
        summary={
          <Text variant="t16Medium">
            {t('template0:title5')}{' '}
            <Text component="span" variant="inputSmall" sx={{ color: COLORS.grey400 }}>
              ({t('template0:subTitle4')})
            </Text>
          </Text>
        }
        details={
          <>
            <Controller
              control={control}
              name={isScanLimitEnabledPath}
              render={({ field: { value, onChange, onBlur } }) => {
                return (
                  <AsyncSwitch
                    id={isScanLimitEnabledPath}
                    sx={{ marginBottom: '8px' }}
                    checked={value}
                    onBlur={onBlur}
                    onChange={(event) => {
                      onChange(event);

                      if (!event.target.checked) {
                        clearErrors(scanLimitPath);
                      }
                    }}
                    label={t('template0:sliderTitle2')}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name={scanLimitPath}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                return (
                  <TextInput
                    id={scanLimitPath}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!error?.message}
                    helperText={error?.message}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', type: 'number' }}
                    fullWidth
                    disabled={!isScanLimitsEnabled}
                    sx={{
                      mb: 2,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                    }}
                  />
                );
              }}
            />
          </>
        }
      />
    </CreateQRContentBlockLayout>
  );
}
